import type { TFunction } from 'i18next';

import {
    AssumptionDto,
    MeasureRationaleDto,
    PrognosisMetricKey,
    PrognosisWarningDto,
    ProposedMeasureDto,
} from './ensan-steckbrief.dto';
import BUILDING_FUNCTIONS_MAPPING from 'src/common/buildingFunctions.json';
import {
    formatInstant,
    heatingUsageProfileLabel,
    pvEstimateReasonLabel,
    pvRoofSourceLabel,
} from './steckbrief-investment';
import {
    ENERGY_CARRIER_OPTIONS,
    LEGACY_STAMP_FIELD_LABEL_KEYS,
    PROGNOSIS_METRICS,
    heatingSystemLabel,
    measureLabel,
    variantFieldByKey,
} from './steckbrief-variant-fields';

/**
 * Client-side rendering of the `/calculate` message codes (GEN-758): the
 * backend API is language-agnostic — an assumption entry or a measure
 * rationale travels as a stable machine code plus a small `params` object of
 * RAW values (numbers unformatted, enum-ish values as their stable enum
 * names), and THIS module translates it into the active UI language.
 *
 * Assumption messages: the de catalog texts are byte-moves of the German
 * display texts that lived in the backend `AssumptionCode.kt` until GEN-758
 * (provenance: that enum's `displayText` fields, with the server-interpolated
 * constants replaced by i18next placeholders); the en texts are the removed
 * `displayTextEn` wordings. Rationale messages: the en texts are the rule
 * table's English strings removed from `MeasureRecommendationService.kt`; the
 * de texts are NEW translations (the rationale never had German wording — this
 * closes the German-UI-shows-English gap of GEN-694).
 *
 * The VALUES stay backend-owned: constants (CO₂ factors, prices, the NGF
 * factor, the assumed heating age, the savings spread, the rule thresholds)
 * arrive as params sourced from EnergyConstants/RecommendationConstants at the
 * backend firing sites — they can never desync from the message the client
 * renders; only the WORDING and the per-locale number formatting (de comma,
 * en point) live here.
 *
 * Prognosis warnings (GEN-788): the en texts are the English of the calibrated
 * sheet's Warnings tab ("Admi_Vorprognose_Rechner" v6), trimmed only of the
 * investment (H2) sentences — the Steckbrief's investment is GEN-780's
 * item-by-item estimate, not the sheet's — and adapted where the sheet names an
 * input the Steckbrief does not have (an invoice, a sheet cell), with the live
 * values and tested thresholds as params; the de texts translate them.
 * Reference-set narrative figures ("3 of 35", "17 percentage points", "103 %")
 * stay literal catalog text — a sheet iteration updates them through the
 * dev-context runbook (iterate-ensan-prognosis-model.md).
 *
 * An UNKNOWN code (newer backend) renders honestly as the raw code plus its
 * params — never a blank row or tooltip (see [assumptionMessage] /
 * [measureRationale] / [warningTexts]).
 */

/**
 * Fraction-digit floors mirroring the backend's former display formatting
 * (`AssumptionCode.kt`, MIN_FRACTION_DIGITS): a price or factor reads
 * "0,30"/"0.30", never "0,3"/"0.3" — more digits only when the raw value
 * carries more precision (capped like the backend's MAX_FRACTION_DIGITS).
 */
const VALUE_MIN_FRACTION_DIGITS = 2;
const MAX_FRACTION_DIGITS = 6;

/**
 * Locale-formatted decimal for a numeric assumption param: de comma / en
 * point, no grouping — the client-side counterpart of the removed backend
 * `Language.number` formatter, fed the RAW JSON number from `params`.
 */
export function formatAssumptionNumber(language: string | undefined, value: number, minFractionDigits: number): string {
    const locale = language !== undefined && language.startsWith('de') ? 'de-DE' : 'en-US';
    return value.toLocaleString(locale, {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: MAX_FRACTION_DIGITS,
        useGrouping: false,
    });
}

/**
 * Locale-formatted number with EXACTLY `fractionDigits` decimals and grouping
 * (de "12.321" / "0,25", en "12,321" / "0.25") — the prognosis-warning formats
 * (GEN-788): a floor area reads with its thousands separator, a ratio always
 * with its two decimals.
 */
export function formatFixedNumber(language: string | undefined, value: number, fractionDigits: number): string {
    const locale = language !== undefined && language.startsWith('de') ? 'de-DE' : 'en-US';
    return value.toLocaleString(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

/** How one param of a message is rendered before interpolation. */
type AssumptionParamFormat =
    /** Raw JSON number, locale-formatted with the value floor ("0,24"/"0.24"). */
    | 'VALUE'
    /** Raw JSON number that reads as an integer (years, a ± percent) — no forced decimals. */
    | 'INTEGER'
    /** Raw JSON number with its own precision ("0,1", "705,37") — no forced decimals, no grouping (GEN-788). */
    | 'DECIMAL'
    /** Raw JSON number rounded to a whole number, with grouping ("12.321", GEN-788). */
    | 'ROUNDED'
    /** Raw JSON number with exactly one decimal, with grouping ("149,6", GEN-788). */
    | 'FIXED_1'
    /** Raw JSON number with exactly two decimals, with grouping ("0,25", GEN-788). */
    | 'FIXED_2'
    /** Raw JSON number with exactly three decimals — a tariff as the sheet prints it ("0,069", GEN-788). */
    | 'FIXED_3'
    /** An `EnergyCarrier` enum name, translated via the carrier option labels. */
    | 'CARRIER'
    /** A `RecommendedHeatingSystem` enum name, translated via the heating-system option labels (GEN-780). */
    | 'HEATING_SYSTEM'
    /** A `HeatingUsageProfile` enum name, translated via the DIN V 18599-10 profile labels (GEN-780). */
    | 'USAGE_PROFILE'
    /** A `PvRoofSource` enum name → its product name ("Google Solar" / "admi Solar", GEN-799). */
    | 'ROOF_SOURCE'
    /** A `PvEstimateUnavailableReason` enum name, translated via the reason labels (GEN-799). */
    | 'PV_REASON'
    /** An ADV building-function code ("31001_3021") → its register name (GEN-799). */
    | 'BUILDING_FUNCTION'
    /** An ISO instant, formatted as a per-locale date + time (GEN-799). */
    | 'DATETIME';

interface AssumptionMessageSpec {
    /**
     * i18next key (== the English message text, catalog convention) with
     * interpolation placeholders for the value-carrying params.
     */
    labelKey: string;
    /** Formats per param name; params the backend sends beyond these pass through verbatim. */
    params?: Record<string, AssumptionParamFormat>;
    /**
     * Client-derived interpolation values the backend deliberately does NOT
     * send — today the per-carrier source/tariff notes of the collapsed codes:
     * the carrier set is a bounded enum, so the note is client-side wording
     * keyed by the `carrier` param (backend `AssumptionCode` KDoc contract).
     */
    derivedParams?: (t: TFunction, rawParams: Record<string, unknown>) => Record<string, string>;
}

/**
 * Per-carrier source note of [CO2_FACTOR_APPLIED] — client-side wording keyed
 * by the bounded carrier enum. Since GEN-788 the factors are the calibrated
 * prognosis sheet's f_heat (v6 Constants C6:C13) and f_elec (C57), and the
 * notes are the f_heat "source" column of the sheet's v5 (v6 repurposed that
 * column for the EVEBI tariff source). The code fires only when the CO₂ saving
 * was predicted (v6 Constants F "CO2 predictable?") — never for Fernwärme,
 * Holzhackschnitzel, Holzpellets or an unmodelled carrier, so those
 * carriers carry no note; an unknown (future) carrier falls back to the
 * generic GEG source.
 */
const CO2_FACTOR_SOURCE_NOTE_KEYS: Record<string, string> = {
    NATURAL_GAS: 'recovered from the reference reports (GEG 0.240); reference spread 0.234–0.336',
    HEATING_OIL: 'recovered from the reference reports (GEG 0.310)',
    LIQUEFIED_GAS: 'GEG Annex 9, not verified',
    // The building electricity (f_elec) — never a heating carrier since 2026-09-29.
    ELECTRICITY: 'GEG Annex 9; confirmed in 24 of the 36 fossil reference reports',
};

/**
 * Tariff note of [ENERGY_PRICE_APPLIED] (GEN-788, sheet v6): the code fires
 * only for a tariff the cost-saving prognosis took from the EVEBI defaults
 * (Constants E6:E13) because the advisor entered none — every carrier, the
 * electricity price included, reads the same note. The per-carrier standard
 * tariffs of the former cost model (and their notes) are gone from the main
 * run; the Prio column still uses them, but its assumptions are not surfaced.
 */
export const EVEBI_DEFAULT_NOTE_KEY = 'EVEBI default';

/**
 * What the WINDOW_SCORE_ASSUMED window score was derived from (backend `basis`
 * param, GEN-788): the Baujahr proxy for double glazing (only while the window
 * status is not offen/teilsaniert, which would make the proxy collected data),
 * double glazing without any year, or the window-replacement status when the
 * glazing type is unknown.
 */
const WINDOW_SCORE_BASIS_LABEL_KEYS: Record<string, string> = {
    CONSTRUCTION_YEAR: 'double glazing dated by the building construction year (no window-replacement year known)',
    NO_YEAR: 'double glazing without any known year',
    STATUS: 'glazing type not recorded, derived from the window-replacement status',
};

/**
 * Message spec per backend `AssumptionCode` — MIRROR of that enum's code list
 * and params shapes (backend `ensan` module; no compiler enforces the
 * cross-repo match, steckbrief-calculation-messages.test.ts pins the catalogs).
 * Grouped by pipeline station; the DISPLAY order comes from the backend response
 * (its enum declaration order), not from this registry.
 */
export const ASSUMPTION_MESSAGES: Record<string, AssumptionMessageSpec> = {
    // --- facts mapping: saniert without a year → reference year ---
    ROOF_RENOVATION_YEAR_ASSUMED_RECENT: {
        labelKey: 'Roof recorded as renovated without a year — renovation year assumed to be the current year.',
    },
    FACADE_RENOVATION_YEAR_ASSUMED_RECENT: {
        labelKey: 'Facade recorded as renovated without a year — renovation year assumed to be the current year.',
    },
    BASEMENT_INSULATION_YEAR_ASSUMED_RECENT: {
        labelKey:
            'Basement ceiling recorded as insulated without a year — insulation year assumed to be the current year.',
    },
    WINDOW_REPLACEMENT_YEAR_ASSUMED_RECENT: {
        labelKey: 'Windows recorded as replaced without a year — replacement year assumed to be the current year.',
    },
    // --- facts mapping: unknown heating year of a young building (QA feedback round) ---
    // The heating year is unknown (or its gate says No) and the building is at
    // most `maxBuildingAgeYears` old: the heating year is taken to be the Baujahr.
    HEATING_YEAR_FROM_CONSTRUCTION_YEAR: {
        labelKey:
            'Heating construction year unknown — assumed equal to the construction year (building at most {{maxBuildingAgeYears}} years old).',
        params: { maxBuildingAgeYears: 'INTEGER' },
    },

    // --- measure rules ---
    // Rule-level (measure set) for the measure rules; since GEN-788 also
    // building-level (no measure) when the energy prognosis ran — Denkmalschutz
    // widens its band.
    MONUMENT_PROTECTION_ASSUMED_ABSENT: {
        labelKey: 'Monument protection not recorded — no monument protection assumed.',
    },
    ROOF_RENOVATION_ASSUMED_ABSENT: {
        labelKey: 'Roof renovation not recorded — roof assumed not renovated.',
    },
    FACADE_RENOVATION_ASSUMED_ABSENT: {
        labelKey: 'Facade renovation not recorded — facade assumed not renovated.',
    },
    BASEMENT_INSULATION_ASSUMED_ABSENT: {
        labelKey: 'Basement-ceiling insulation not recorded — basement ceiling assumed not insulated.',
    },
    WINDOW_YEAR_FROM_CONSTRUCTION_YEAR: {
        labelKey:
            'No window-replacement year known — windows assumed original (building construction year used as the window year).',
    },
    WINDOW_CONDITION_INFERRED_FROM_YEAR: {
        labelKey: 'Glazing type not recorded — window condition inferred from the window year.',
    },
    // 2026-09-29: neither a window year nor the Baujahr — windows assumed old
    // enough; recommended, never Prio.
    WINDOW_YEAR_UNKNOWN_ASSUMED_OLD: {
        labelKey:
            'Neither a window-replacement year nor the building construction year is known — windows assumed old enough to need replacement.',
    },
    // Since 2026-09-29 recorded by the heat pump's Prio decision (the age no
    // longer gates the recommendation itself).
    HEATING_AGE_ASSUMED: {
        labelKey: 'Heating construction year not recorded — heating age of {{years}} years assumed.',
        params: { years: 'INTEGER' },
    },
    // HEATING_CARRIER_ASSUMED_NOT_EXCLUDED became HEATING_CARRIER_ASSUMED_FOSSIL
    // with the fossil-only heat-pump rule (2026-09-29).
    HEATING_CARRIER_ASSUMED_FOSSIL: {
        labelKey:
            'Heating energy carrier not recorded — assumed to be fossil-fired (natural gas, heating oil or liquefied gas).',
    },
    SUN_PROTECTION_ASSUMED_ABSENT: {
        labelKey: 'Sun protection not recorded — assumed not present.',
    },
    // PV_ASSUMED_ABSENT: retired with GEN-777, back with the PV rule
    // ("Steckbrief recommendations v2" sheet).
    PV_ASSUMED_ABSENT: {
        labelKey: 'Renewable energy sources not recorded — no PV system assumed.',
    },
    LIGHTING_ASSUMED_LEGACY: {
        labelKey: 'Lighting not recorded — assumed legacy stock (no LED).',
    },
    MVHR_FUNCTION_ASSUMED_NOT_EXCLUDED: {
        labelKey: 'Building function not recorded — function assumed not excluded from the ventilation recommendation.',
    },

    // --- Ersteinschätzung derivations ---
    // Since GEN-788 also when the estimated NGF fed the prognosis' demand estimate.
    // GEN-806: SALES without NGF and BGF — the BGF comes from the CityGML building model.
    GROSS_FLOOR_AREA_FROM_BUILDING_MODEL: {
        labelKey:
            'Gross floor area not recorded — estimated from the 3D building model (ground area × storeys): {{grossFloorArea}} m².',
        params: { grossFloorArea: 'ROUNDED' },
    },
    STOREYS_ESTIMATED_FROM_HEIGHT: {
        labelKey: 'Storey count not recorded in the 3D building model — estimated from the building height.',
    },
    NET_FLOOR_AREA_ESTIMATED_FROM_GROSS: {
        labelKey: 'Net floor area not recorded — estimated from the gross floor area (NGF = {{factor}} × BGF).',
        params: { factor: 'VALUE' },
    },
    DEFAULT_ORIENTATION_BANDS: {
        labelKey:
            'Building function not recorded or without stored reference values — classified using the default reference values (event buildings/community centers).',
    },
    // HEAT_CARRIER_ASSUMED_OTHER and SAVINGS_COMBINED_MULTIPLICATIVELY retired
    // with GEN-788 (sheet v6): the printed cost saving is the calibrated H3, so
    // the main run no longer prices a missing carrier with the "other" tariff
    // nor combines per-measure cost savings; the Prio column still does, but
    // its assumptions are not surfaced. A stale backend still sending either
    // renders through the unknown-code fallback.
    // GEN-788: fires when the CO₂ saving was predicted — once for the heat
    // carrier's f_heat, once for the building electricity (ELECTRICITY).
    CO2_FACTOR_APPLIED: {
        labelKey: 'CO₂ factor {{carrier}}: {{value}} kg CO₂e/kWh ({{source}}).',
        params: { carrier: 'CARRIER', value: 'VALUE' },
        derivedParams: (t, raw) => ({
            ...electricityAsEnergy(t, raw),
            source: t(CO2_FACTOR_SOURCE_NOTE_KEYS[String(raw['carrier'])] ?? 'GEG Annex 9'),
        }),
    },
    // DISTRICT_HEATING_ASSUMED_KWK retired with GEN-788: the prognosis never
    // predicts Fernwärme CO₂ (the network factor is unknown — the
    // CO2_NOT_PREDICTED_DISTRICT_HEATING warning says so), so no KWK factor is
    // assumed any more. A stale backend still sending it renders through the
    // unknown-code fallback.

    // HEAT_PUMP_ACCOUNTED_AS_ELECTRICITY removed with the Strom (Wärmepumpe)
    // heating carrier (2026-09-29, not supported) — it can no longer fire; a
    // stale backend still sending it renders through the unknown-code fallback.
    // GEN-788 (sheet v6 H3): fires when the cost saving was predicted with an
    // EVEBI default tariff — for the heating carrier when no heat price was
    // entered, for ELECTRICITY when no electricity price was entered.
    ENERGY_PRICE_APPLIED: {
        labelKey: 'Energy price {{carrier}}: {{value}} €/kWh ({{note}}).',
        params: { carrier: 'CARRIER', value: 'VALUE' },
        derivedParams: (t, raw) => ({ ...electricityAsEnergy(t, raw), note: t(EVEBI_DEFAULT_NOTE_KEY) }),
    },

    // --- prognosis model (GEN-788): the calibrated sheet's inputs ---
    // An UNKNOWN envelope status is scored 2 ("uninsulated"); teilsaniert and
    // offen are collected data and fire nothing.
    ROOF_CONDITION_ASSUMED_UNRENOVATED: {
        labelKey: 'Roof condition not recorded — scored as uninsulated in the prediction model (score {{score}}).',
        params: { score: 'INTEGER' },
    },
    FACADE_CONDITION_ASSUMED_UNRENOVATED: {
        labelKey: 'Facade condition not recorded — scored as uninsulated in the prediction model (score {{score}}).',
        params: { score: 'INTEGER' },
    },
    BASEMENT_CEILING_CONDITION_ASSUMED_UNRENOVATED: {
        labelKey:
            'Basement-ceiling condition not recorded — scored as uninsulated in the prediction model (score {{score}}).',
        params: { score: 'INTEGER' },
    },
    // One firing per run; `basis` names what the window score was derived from.
    WINDOW_SCORE_ASSUMED: {
        labelKey: 'Window score {{score}} assumed in the prediction model — {{basis}}.',
        params: { score: 'INTEGER' },
        derivedParams: (t, raw) => {
            const basis = String(raw['basis']);
            const labelKey = WINDOW_SCORE_BASIS_LABEL_KEYS[basis];
            return { basis: labelKey === undefined ? basis : t(labelKey) };
        },
    },
    // Fires whenever the demand estimate fed an output (the cost saving, the CO₂
    // saving, or the district-heating rule's energy saving) — the constants are the sheet's
    // demand estimator (Constants B65–B68), sent as raw params.
    ENERGY_DEMAND_ESTIMATED_FROM_FLOOR_AREA: {
        labelKey:
            'Final energy demand estimated from the net floor area and the envelope score, not from the meter: {{coefficient}} × NGF^{{ngfExponent}} × e^({{envelopeCoefficient}} × envelope score), of which {{electricitySharePercent}} % building electricity.',
        params: {
            coefficient: 'DECIMAL',
            ngfExponent: 'DECIMAL',
            envelopeCoefficient: 'DECIMAL',
            electricitySharePercent: 'DECIMAL',
        },
    },

    // --- window-area derivation (GEN-774, layered defaults GEN-802) ---
    // Fire on the derived window area when the share was absent or equal to
    // the default configured for the Gebäudetyp — at BASE level (type missing
    // or without its own row) or at BUILDING_FUNCTION level. Params per firing:
    // the resolved percent, plus the raw ADV code on the per-type code.
    WINDOW_AREA_SHARE_DEFAULT: {
        labelKey:
            'Building type not recorded or without its own window area share — general default of {{percent}} % of the real wall surface applied.',
        params: { percent: 'INTEGER' },
    },
    WINDOW_AREA_SHARE_BUILDING_FUNCTION_DEFAULT: {
        labelKey:
            'Window area share left at the default for {{buildingFunction}} — {{percent}} % of the real wall surface applied.',
        params: { percent: 'INTEGER', buildingFunction: 'BUILDING_FUNCTION' },
    },

    // --- investment estimate (GEN-780) ---
    // Fires only when the derived rung produced the effective hourly heat consumption
    // (no explicit value, a positive newest heat consumption): kWh/a ÷ the
    // yearly heating hours (h/a) of the building function's DIN V 18599-10
    // usage profile — profile code and hours are the backend's.
    HEAT_PUMP_POWER_ESTIMATED: {
        labelKey:
            'Hourly heat consumption estimated from the newest heat consumption ÷ {{heatingHours}} heating hours per year (DIN V 18599-10 usage profile {{usageProfile}}).',
        params: { usageProfile: 'USAGE_PROFILE', heatingHours: 'INTEGER' },
    },
    // Fires additionally when that derivation used the DEFAULT profile because
    // the building function is missing or has no mapped profile (residential,
    // Hallenbad — Tabelle 5 has no pool profile —, garages, churches, …).
    HEATING_USAGE_PROFILE_DEFAULTED: {
        labelKey:
            'Building type not recorded or without a DIN V 18599-10 usage profile — default profile {{usageProfile}} ({{heatingHours}} heating hours per year) applied to estimate the hourly heat consumption.',
        params: { usageProfile: 'USAGE_PROFILE', heatingHours: 'INTEGER' },
    },
    // Fires when the heating renewal is PRICED in the selected set while the
    // effective heating system is not a heat pump (an advisor override to
    // Biomasse / Elektrische Heizung, or a legacy stamped Bio-Gas override):
    // only a heat-pump rate exists.
    HEATING_INVESTMENT_PRICED_AS_HEAT_PUMP: {
        labelKey:
            'Heating investment priced at the heat-pump rate although {{heatingSystem}} is selected — no cost data for other heating systems yet.',
        params: { heatingSystem: 'HEATING_SYSTEM' },
    },

    // --- PV investment from the PV Calculator (GEN-799), measure PHOTOVOLTAICS ---
    // Fires when the PV line is PRICED: the ROI-optimal design the calculator
    // returned for the Steckbrief's buildings. `annualUsageKwh` travels only
    // when a consumption was sent (the estimated case fires
    // PV_ANNUAL_USAGE_ESTIMATED instead) — it is deliberately not part of the
    // sentence, so an absent param never leaves a raw placeholder.
    PV_INVESTMENT_FROM_PV_CALCULATOR: {
        labelKey:
            'PV investment taken from the PV Calculator: {{designKwp}} kWp (battery {{batteryKwh}} kWh), {{investmentEur}} € gross, ROI-optimal sizing ({{optimizer}}, cost model {{costModel}}, electricity price {{energyPrice}} €/kWh), roof data from {{roofSource}}, computed {{computedAt}}.',
        params: {
            designKwp: 'DECIMAL',
            batteryKwh: 'DECIMAL',
            investmentEur: 'ROUNDED',
            energyPrice: 'VALUE',
            roofSource: 'ROOF_SOURCE',
            computedAt: 'DATETIME',
        },
    },
    // No electricity consumption entered — the calculator estimated the load
    // profile the ROI sizing needs.
    PV_ANNUAL_USAGE_ESTIMATED: {
        labelKey: 'No electricity consumption entered — the PV Calculator estimated the annual load for the PV sizing.',
    },
    // Google Solar gave nothing for the buildings; admi Solar (CityGML roofs) was used.
    PV_ROOF_SOURCE_FALLBACK: {
        labelKey: 'Google Solar returned no roof data — roof geometry from {{roofSource}} used instead.',
        params: { roofSource: 'ROOF_SOURCE' },
    },
    // Only part of the Steckbrief's building subset had a usable roof.
    PV_ESTIMATE_PARTIAL_COVERAGE: {
        labelKey:
            'PV estimate covers {{buildingsPriced}} of {{buildingsTotal}} buildings of this Steckbrief — the others had no usable roof data.',
        params: { buildingsPriced: 'INTEGER', buildingsTotal: 'INTEGER' },
    },
    // The Steckbrief's Gebäudetyp has no PV Calculator load profile — the
    // substituted ADV code travels as the param.
    PV_BUILDING_FUNCTION_DEFAULTED: {
        labelKey:
            'Building type not supported by the PV Calculator — the load profile of {{buildingFunction}} applied instead.',
        params: { buildingFunction: 'BUILDING_FUNCTION' },
    },
    // The PV line is NOT_AVAILABLE (failed or disabled): the totals leave PV out.
    PV_INVESTMENT_NOT_AVAILABLE: {
        labelKey: 'PV investment not available — {{reason}}. The PV system is excluded from the investment totals.',
        params: { reason: 'PV_REASON' },
    },
    // GEN-770 / GEN-700: an advisor-adjusted Kennzahl. Fixed text only — the Begründung stays in
    // the Variant stamp and is never shown here, so the line can never name a reference building.
    MANUAL_ADJUSTMENT: {
        labelKey: '{{kennzahl}}: manually adjusted — justification in the audit log',
        derivedParams: (t, raw) => ({ kennzahl: kennzahlLabel(t, raw['kennzahl']) }),
    },
    // GEN-700 D4: the matching inputs changed after the stamped lookalike search — a hint, not a gate.
    LOOKALIKE_REFERENCE_CHANGED: {
        labelKey: 'Comparison values are based on changed building data — search again.',
    },
};

/** Backend `PrognosisMetric` names of the adjustable Kennzahlen → their Prognose-panel labels. */
const KENNZAHL_LABEL_KEYS: Record<string, string> = {
    ENERGY_SAVING_POTENTIAL: 'Energy saving potential',
    CO2_SAVING: 'CO₂ saving',
    COST_SAVING: 'Cost saving',
    INVESTMENT_COST_PER_SQM: 'Investment costs per m²',
};

function kennzahlLabel(t: TFunction, value: unknown): string {
    const labelKey = typeof value === 'string' ? KENNZAHL_LABEL_KEYS[value] : undefined;
    return labelKey === undefined ? String(value ?? '') : t(labelKey);
}

/** ADV register names keyed by building-function code (the Gebäudetyp select's names). */
const BUILDING_FUNCTION_NAMES = BUILDING_FUNCTIONS_MAPPING as Record<string, string>;

/**
 * The ELECTRICITY entry of the per-carrier factor/price assumptions is the
 * building's own electricity (f_elec / p_elec, on every building), so it reads
 * as plain "Strom" (GEN-788).
 */
function electricityAsEnergy(t: TFunction, raw: Record<string, unknown>): Record<string, string> {
    return raw['carrier'] === 'ELECTRICITY' ? { carrier: t('Electricity') } : {};
}

/** Carrier enum name → the form's translated carrier label; raw name for unknown carriers. */
function carrierLabel(t: TFunction, value: string): string {
    const option = ENERGY_CARRIER_OPTIONS.find((o) => o.value === value);
    return option === undefined ? value : t(option.labelKey);
}

/** A numeric param per its format (see [AssumptionParamFormat]). */
function formatNumberParam(language: string | undefined, format: AssumptionParamFormat | undefined, value: number) {
    switch (format) {
        case 'VALUE':
            return formatAssumptionNumber(language, value, VALUE_MIN_FRACTION_DIGITS);
        case 'ROUNDED':
            return formatFixedNumber(language, value, 0);
        case 'FIXED_1':
            return formatFixedNumber(language, value, 1);
        case 'FIXED_2':
            return formatFixedNumber(language, value, 2);
        case 'FIXED_3':
            return formatFixedNumber(language, value, 3);
        default:
            return formatAssumptionNumber(language, value, 0);
    }
}

/**
 * One param, rendered per its spec format. A param the spec does not list
 * (additive backend) still renders legibly: numbers locale-formatted without a
 * decimal floor, everything else verbatim.
 */
function formatParam(
    t: TFunction,
    language: string | undefined,
    format: AssumptionParamFormat | undefined,
    value: unknown,
): string {
    if (typeof value === 'number') return formatNumberParam(language, format, value);
    if (typeof value === 'string' && format === 'CARRIER') return carrierLabel(t, value);
    if (typeof value === 'string' && format === 'HEATING_SYSTEM') return heatingSystemLabel(t, value);
    if (typeof value === 'string' && format === 'USAGE_PROFILE') return heatingUsageProfileLabel(t, value);
    if (typeof value === 'string' && format === 'ROOF_SOURCE') return pvRoofSourceLabel(value);
    if (typeof value === 'string' && format === 'PV_REASON') return pvEstimateReasonLabel(t, value);
    if (typeof value === 'string' && format === 'BUILDING_FUNCTION') return BUILDING_FUNCTION_NAMES[value] ?? value;
    if (typeof value === 'string' && format === 'DATETIME') return formatInstant(language, value);
    return String(value);
}

/**
 * Honest fallback for a code this build does not know (newer backend): the raw
 * code plus its raw params — legible and truthful, never a blank row. Shared by
 * the assumption entries and the prognosis warnings (GEN-788).
 */
function unknownCodeFallback(entry: { code: string; params?: Record<string, unknown> | null }): string {
    const params = entry.params ?? {};
    const entries = Object.entries(params).map(([key, value]) => `${key}: ${String(value)}`);
    return entries.length === 0 ? entry.code : `${entry.code} (${entries.join(', ')})`;
}

/** The spec's params formatted per locale, the derived client-side params on top. */
function formatSpecParams(
    t: TFunction,
    language: string | undefined,
    spec: Pick<AssumptionMessageSpec, 'params' | 'derivedParams'>,
    rawParams: Record<string, unknown>,
): Record<string, string> {
    const formatted: Record<string, string> = {};
    for (const [key, value] of Object.entries(rawParams)) {
        formatted[key] = formatParam(t, language, spec.params?.[key], value);
    }
    Object.assign(formatted, spec.derivedParams?.(t, rawParams));
    return formatted;
}

/**
 * The display text of one `/calculate` assumption entry (GEN-758): the i18next
 * message of its stable code with the raw params formatted per the active UI
 * language — numbers de-comma/en-point, carriers through the form's carrier
 * labels. The backend no longer sends display strings; unknown codes fall back
 * to code + params (see [unknownCodeFallback]).
 */
export function assumptionMessage(t: TFunction, language: string | undefined, assumption: AssumptionDto): string {
    const spec = ASSUMPTION_MESSAGES[assumption.code];
    if (spec === undefined) return unknownCodeFallback(assumption);
    return t(spec.labelKey, formatSpecParams(t, language, spec, assumption.params ?? {}));
}

/**
 * Chips of the form field and/or measure a code-based entry names (an
 * assumption, GEN-756, or a prognosis warning, GEN-788) — a rule-level entry
 * carries BOTH (the missing field and the measure whose rule assumed it), a
 * standing model assumption carries neither. Keyed by kind + code, never by
 * label: since GEN-777 a field and its measure can share one label
 * ("Sonnenschutz", "Kellerdämmung").
 */
export function affectedChips(
    t: TFunction,
    entry: { affectedFormKey?: string | null; measure?: string | null },
): { key: string; label: string }[] {
    const chips: { key: string; label: string }[] = [];
    if (entry.affectedFormKey) {
        const labelKey =
            variantFieldByKey(entry.affectedFormKey)?.labelKey ?? LEGACY_STAMP_FIELD_LABEL_KEYS[entry.affectedFormKey];
        chips.push({
            key: `field-${entry.affectedFormKey}`,
            label: labelKey === undefined ? entry.affectedFormKey : t(labelKey),
        });
    }
    if (entry.measure) {
        chips.push({ key: `measure-${entry.measure}`, label: measureLabel(t, entry.measure) });
    }
    return chips;
}

// ---------------------------------------------------------------------------
// Measure rationales (the proposal-chip tooltip)
// ---------------------------------------------------------------------------

/**
 * Message spec per backend `MeasureRationale` key — MIRROR of that enum's key
 * list and params shapes (backend `MeasureRecommendationService.kt`). One key
 * per rule row of the EB-validated rule table (measures sheet v2); a key names
 * the CONDITION, not the measure. The en texts are the rule table's former
 * English rationale strings; the numeric thresholds travel as raw params.
 */
export const MEASURE_RATIONALE_MESSAGES: Record<string, AssumptionMessageSpec> = {
    FACADE_UNINSULATED_ERA: {
        labelKey:
            'Building stock from {{thresholdYear}} or earlier, the facade has not been renovated since, and the building is not monument-protected',
        params: { thresholdYear: 'INTEGER' },
    },
    ROOF_UNINSULATED_ERA: {
        labelKey: 'Building stock from {{thresholdYear}} or earlier and the roof has not been renovated since',
        params: { thresholdYear: 'INTEGER' },
    },
    BASEMENT_UNINSULATED_ERA: {
        labelKey:
            'Building stock from {{thresholdYear}} or earlier and the basement ceiling has not been insulated since',
        params: { thresholdYear: 'INTEGER' },
    },
    SUN_PROTECTION_MISSING: {
        labelKey: 'No sun protection present',
    },
    WINDOWS_SINGLE_PANE_OR_OLD: {
        labelKey: 'Windows are single-pane or date from {{thresholdYear}} or earlier',
        params: { thresholdYear: 'INTEGER' },
    },
    HEATING_CARRIER_FOSSIL: {
        labelKey: 'Heating is fossil-fired (natural gas, heating oil or liquefied gas)',
    },
    PV_ABSENT: {
        labelKey: 'No PV system present',
    },
    // PUMPS_DUE and PIPE_INSULATION_DUE retired with their measures' proposal
    // rules (GEN-777) — the keys can no longer fire (PV_ABSENT is back with the
    // PV rule); HEATING_OLDER_THAN_RENEWAL_AGE became HEATING_CARRIER_FOSSIL
    // when the heat-pump rule dropped its age condition (2026-09-29);
    // RECOMMENDED_FOR_EVERY_BUILDING stays (Heizungsoptimierung is still
    // unconditional). A stale backend still sending a retired key renders
    // through the unknown-key fallback.
    RECOMMENDED_FOR_EVERY_BUILDING: {
        labelKey: 'Recommended by default for every building',
    },
    LIGHTING_NOT_LED: {
        labelKey: 'Lighting is not LED',
    },
    BUILDING_OLD_AND_NOT_MVHR_EXCLUDED: {
        labelKey: 'Building older than {{minAgeYears}} years and its function is not on the MVHR exclusion list',
        params: { minAgeYears: 'INTEGER' },
    },
};

/**
 * The heating-system choice fragment of the heating-renewal rationale, keyed
 * by the PROPOSED `RecommendedHeatingSystem` (backend contract: the reason is
 * 1:1 with the returned system — only the auto-proposable systems can appear
 * here). Since GEN-780 (Bio-Gas no longer offered) the rule ALWAYS proposes
 * the heat pump, whatever the monument protection: the fragment names the
 * choice without the former Denkmalschutz reason, and the Bio-Gas fragments
 * (monument protection → Bio-Gas ± Solarthermie) are gone with their branches.
 * An unknown (future) system simply renders no fragment.
 */
export const HEATING_CHOICE_RATIONALE_KEYS: Record<string, string> = {
    HEAT_PUMP: 'heat pump proposed',
};

/** [unknownCodeFallback] for a rationale key this build does not know. */
function unknownRationaleFallback(rationale: MeasureRationaleDto): string {
    const entries = Object.entries(rationale.params ?? {}).map(([key, value]) => `${key}: ${String(value)}`);
    return entries.length === 0 ? rationale.code : `${rationale.code} (${entries.join(', ')})`;
}

/**
 * The tooltip text of one proposed measure (GEN-758): the i18next message of
 * its stable rationale key with the raw threshold params locale-formatted —
 * plus, on the heating-renewal proposal, the choice fragment keyed by the
 * proposed heating system (mirroring the backend's former " — " composition).
 * Unknown rationale keys fall back to key + params, never a blank tooltip.
 */
export function measureRationale(t: TFunction, language: string | undefined, measure: ProposedMeasureDto): string {
    const spec = MEASURE_RATIONALE_MESSAGES[measure.rationale.code];
    let text: string;
    if (spec === undefined) {
        text = unknownRationaleFallback(measure.rationale);
    } else {
        text = t(spec.labelKey, formatSpecParams(t, language, spec, measure.rationale.params ?? {}));
    }
    const choiceKey = measure.heatingSystem === null ? undefined : HEATING_CHOICE_RATIONALE_KEYS[measure.heatingSystem];
    return choiceKey === undefined ? text : `${text} — ${t(choiceKey)}`;
}

// ---------------------------------------------------------------------------
// Prognosis warnings (GEN-788) — the calibrated sheet's applicability checks
// ---------------------------------------------------------------------------

/**
 * i18next label keys of the prognosis model's H1 rules (backend
 * `PrognosisBranch`): the sheet's fossil / district / renewable rule, named by
 * what the renovation does — shared by the H1_CLAMPED warning and the
 * "Prediction model" block.
 */
export const PROGNOSIS_BRANCH_LABEL_KEYS: Record<string, string> = {
    FOSSIL: 'heat-pump rule',
    DISTRICT: 'district-heating rule',
    RENEWABLE: 'envelope-only rule',
};

/** i18next label keys of the CO₂ suppression reasons (backend `co2SuppressionReason`). */
export const CO2_SUPPRESSION_REASON_LABEL_KEYS: Record<string, string> = {
    DISTRICT_NETWORK_FACTOR_UNKNOWN: 'district-heating network factor unknown',
    WOOD_BASELINE_TOO_SMALL: 'wood baseline too small to predict',
    WOOD_HEAT_PUMP_STRADDLES_ZERO: 'wood replaced by a heat pump — the result straddles zero',
    CARRIER_NOT_MODELLED: 'energy carrier not in the prediction model',
    HEATING_SYSTEM_NOT_MODELLED: 'heating system not in the prediction model',
};

/** Which clamp bound H1_CLAMPED hit (backend `bound` param). */
const CLAMP_BOUND_LABEL_KEYS: Record<string, string> = {
    LOW: 'bottom of the range',
    HIGH: 'top of the range',
};

/** The meter readings NO_METER_READING lists as missing (backend `missing` param) — the form labels. */
const MISSING_METER_LABEL_KEYS: Record<string, string> = {
    HEAT: 'Heat consumption',
    ELECTRICITY: 'Electricity consumption',
};

/**
 * CARRIER_NOT_MODELLED without a `carrier` param: the field is blank, or it
 * holds (READI-prefilled) free text the backend facts mapper matches to no
 * carrier — the select still shows that text, so the notice says both.
 */
const UNKNOWN_CARRIER_KEY = 'none recorded or not recognised';

/** Translated label of a bounded enum-ish param — the raw value for an unknown (future) one. */
function enumParamLabel(t: TFunction, labelKeys: Record<string, string>, value: unknown): string {
    const labelKey = labelKeys[String(value)];
    return labelKey === undefined ? String(value) : t(labelKey);
}

/** Shared action text of the CO₂ notices that leave the energy and cost savings untouched. */
const CO2_ONLY_ACTION_KEY =
    'Nothing to fix. The energy saving potential and the cost saving are unaffected and still apply.';

/**
 * One prognosis warning's four texts — the columns of the sheet's Warnings tab
 * (Warning · When it fires · What it means · What to do). Each key is an
 * i18next key (== its English text, catalog convention); the trigger reads
 * LIVE: it interpolates the value that fired and the threshold the engine
 * tested, both raw backend params.
 */
interface WarningMessageSpec {
    titleKey: string;
    triggerKey: string;
    /**
     * The trigger for a run whose backend left out an OPTIONAL live param it
     * could not compute (the heat-pump CO₂ break-even needs a demand estimate,
     * i.e. an NGF) — [triggerKey] would otherwise print the raw `{{param}}`
     * placeholder (i18next leaves a missing variable in the text).
     */
    triggerWithoutParam?: { param: string; triggerKey: string };
    meaningKey: string;
    actionKey: string;
    /** Formats per param name; params the backend sends beyond these pass through verbatim. */
    params?: Record<string, AssumptionParamFormat>;
    /** Client-side labels of the bounded enum-ish params (rule, clamp bound, missing meters, …). */
    derivedParams?: (t: TFunction, rawParams: Record<string, unknown>) => Record<string, string>;
}

/**
 * Message spec per backend `PrognosisWarningCode` — MIRROR of that enum's code
 * list and params shapes (declaration order = the backend's display order; no
 * compiler enforces the cross-repo match, steckbrief-calculation-messages.test.ts
 * pins the catalogs). The sheet code of each warning is noted for traceability
 * (dev-context features/ensan-steckbrief/prognosis-model/warnings.md lists every
 * trimmed sentence and sheet erratum). All 17 sheet warnings are in scope since
 * the cost saving is the sheet v6's H3 — CO2-NA split into three codes, plus
 * the two admi codes: 21 codes.
 */
export const WARNING_MESSAGES: Record<string, WarningMessageSpec> = {
    // CLAMP — never for the 96 % ceiling or the 0 floor, only the rule's own clamp.
    H1_CLAMPED: {
        titleKey: 'Energy saving potential is clamped, not fitted',
        triggerKey:
            'The predicted energy saving hit the {{bound}} the {{branch}} allows ({{clampLowPercent}}–{{clampHighPercent}} %).',
        meaningKey:
            'The formula wanted a value no reference building ever showed, so it was capped. What you see is the boundary, not a calculation.',
        actionKey:
            "Read it as 'at least this much' or 'at most this much'. Do not treat the exact figure as meaningful, and do not compare two clamped buildings against each other.",
        params: { clampLowPercent: 'DECIMAL', clampHighPercent: 'DECIMAL' },
        derivedParams: (t, raw) => ({
            branch: enumParamLabel(t, PROGNOSIS_BRANCH_LABEL_KEYS, raw['branch']),
            bound: enumParamLabel(t, CLAMP_BOUND_LABEL_KEYS, raw['bound']),
        }),
    },
    // DH-RANGE — the driver is the ESTIMATED demand per m² (sheet erratum: the
    // Warnings tab says "heat use" / "meter reading").
    DISTRICT_DEMAND_OUT_OF_RANGE: {
        titleKey: 'District heating rule out of range',
        triggerKey:
            'A district-heated building whose estimated heat demand of {{specificHeatDemandEstimate}} kWh/(m²a) is below {{min}} or above {{max}} kWh per m² per year.',
        meaningKey:
            'For district heat the prediction is driven by the estimated heat demand per m², and that rule was built from buildings between {{min}} and {{max}}. Outside that it is guessing.',
        actionKey:
            'Check the NGF first — an out-of-range value is usually a wrong floor area. If it is right, widen your expectations.',
        params: { specificHeatDemandEstimate: 'FIXED_1', min: 'DECIMAL', max: 'DECIMAL' },
    },
    // ENV
    FOSSIL_ENVELOPE_OUT_OF_RANGE: {
        titleKey: 'Fossil envelope out of range',
        triggerKey:
            'A building on the heat-pump (fossil) rule whose envelope score of {{envelopeScore}} is below {{min}}, i.e. already in fairly good condition.',
        meaningKey:
            'Only 3 of 35 fossil reference buildings were in that good a state, so there is almost no evidence behind this prediction.',
        actionKey:
            'A building already renovated has less left to save, so expect the true figure at the low end. This is a good candidate for a real Energieberatung rather than a prediction.',
        params: { envelopeScore: 'DECIMAL', min: 'DECIMAL' },
    },
    // NEGATIVE — only the cost saving can go negative here (the energy and CO₂
    // points are floored, the sheet's H2 is not the Steckbrief's investment).
    // The sheet's literal tariffs (heating oil 0.099, wood 0.069 — v5 values)
    // are left out, the Prediction model block shows the ones used; the fuels
    // named are the ones that drive this code at the v6 defaults (liquefied
    // gas, wood chips — heating oil and pellets now sit near Erdgas; erratum 15
    // in dev-context's warnings.md).
    NEGATIVE_PREDICTION: {
        titleKey: 'A prediction is negative',
        triggerKey:
            'The POINT estimate of the cost saving comes out below zero (the energy and CO₂ savings never do — they are floored at zero).',
        meaningKey:
            'The renovation makes the running costs WORSE, not better — almost always because a heat pump replaces a cheap fuel (liquefied gas, wood chips) with far more expensive electricity. The building saves energy but pays more for what it still needs.',
        actionKey:
            "Check both tariffs are the municipality's real ones. Then treat it as a genuine finding rather than an error — some buildings really do not pay back on running costs, and saying so early is the point of a pre-analysis.",
    },
    // NEG-LOW — the sheet's "46 percentage points" is its v5 H3 band (v6 runs
    // the energy saving's band through the cost arithmetic), so it is left out.
    NEGATIVE_LOWER_BOUND: {
        titleKey: "A prediction's LOWER BOUND is negative",
        triggerKey: 'The point estimate of the cost saving is positive, but the low end of its range is below zero.',
        meaningKey:
            "The model's central case is still a saving, but it cannot rule out the renovation costing the municipality money every year. That is a materially different message from a confident positive number, and it is the one case where quoting the point value alone is misleading.",
        actionKey:
            'Quote the range, not the point. If the downside matters to the decision, this building is a candidate for a real Energieberatung rather than a pre-analysis.',
    },
    // BILL-UP — fires with NEGATIVE (both: H3 point < 0); "entered" → "used"
    // (the price may be the EVEBI default), "p_elec" → "the electricity price".
    ENERGY_BILL_MAY_RISE: {
        titleKey: 'Energy bill may RISE',
        triggerKey: 'The predicted cost saving comes out negative.',
        meaningKey:
            'At the electricity price used, running the building all-electric costs more than the current fuel does, even after the energy saving. Most common when the current fuel is cheap (gas, wood chips) or the electricity price is high.',
        actionKey:
            "First check that the electricity price is the municipality's real contract price. If it is, this is a real result: the building saves energy and CO₂ but not money. Say so rather than hiding it.",
    },
    // LOW-SAVE — the share and the calibration range are params; "the current
    // bill" is the estimated energy costs (sheet C10: never the meter).
    COST_SAVING_BELOW_CALIBRATION: {
        titleKey: 'Cost saving below calibration',
        triggerKey:
            'The predicted cost saving is {{savingSharePercent}} % of the estimated energy costs — under {{minPercent}} %.',
        meaningKey:
            'The band was fitted on reference buildings saving between {{minPercent}} % and {{maxPercent}} % of their bill. Below that range it is extrapolating, and even the direction is not reliable.',
        actionKey: "Report it as 'little or no cost saving' rather than quoting the number.",
        params: { savingSharePercent: 'FIXED_1', minPercent: 'DECIMAL', maxPercent: 'DECIMAL' },
    },
    // PRICE — adapted: the Steckbrief has no invoice, the energy costs are the
    // demand estimate × the tariffs (sheet C10), so the check compares them with
    // the meters; the stale "(gas around 0.14, …)" and the H2 note are left out.
    IMPLIED_PRICE_UNUSUAL: {
        titleKey: 'Implied current price unusual',
        triggerKey:
            'The estimated energy costs divided by the total metered energy come to {{impliedPrice}} €/kWh — outside {{min}}–{{max}} €/kWh.',
        meaningKey:
            'The cost estimate and the meter readings do not look like they describe the same thing. A real blended tariff sits near 0.10–0.25 €/kWh.',
        actionKey:
            "Check that the meters cover exactly this building and a full year, and that the tariffs are the municipality's real ones. This affects the cost saving only.",
        params: { impliedPrice: 'FIXED_3', min: 'VALUE', max: 'VALUE' },
    },
    // ELEC% — trimmed: the bill-scope sentence (the Steckbrief has no invoice —
    // the energy costs are an estimate).
    ELECTRICITY_SHARE_HIGH: {
        titleKey: 'Electricity share too high',
        triggerKey:
            'Electricity is {{electricitySharePercent}} % of total metered energy — more than {{maxPercent}} % (the reference reports sit near 2 %).',
        meaningKey:
            'Not necessarily wrong — some buildings really are electricity-heavy. But this is exactly the case where the assumption about Nutzerstrom matters most.',
        actionKey: 'For a strongly electricity-heavy building, treat the cost and CO₂ figures as indicative only.',
        params: { electricitySharePercent: 'FIXED_1', maxPercent: 'DECIMAL' },
    },
    // CO2-NA (Fernwärme) — the action adds the supplier-factor path of
    // Constants A19 / Notes B35 (the GEN-782 override); H2 trimmed.
    CO2_NOT_PREDICTED_DISTRICT_HEATING: {
        titleKey: 'CO₂ not predicted: district heating',
        triggerKey: 'The building runs on district heating.',
        meaningKey:
            'CO₂ is deliberately suppressed. For district heating the answer depends on whether the NETWORK decarbonises, which no building input can tell us (17 percentage points average error).',
        actionKey:
            'Nothing to fix. The energy saving potential and the cost saving are unaffected and still apply. For a CO₂ figure, ask the supplier or check the district-heating bill, which must state a CO₂ factor under the CO2KostAufG, and enter both bounds with a reason.',
    },
    // CO2-NA (Holzhackschnitzel / Holzpellets, generator kept) —
    // Calculator B70's wood text.
    CO2_NOT_PREDICTED_BIOMASS: {
        titleKey: 'CO₂ not predicted: wood',
        triggerKey: 'The building runs on {{carrier}} and keeps its heat generator.',
        meaningKey:
            'CO₂ is deliberately suppressed. The carrier {{carrier}} emits {{heatFactor}} kg CO₂e per kWh, so there is very little CO₂ to save in the first place and small changes flip the answer: across the 9 wood reference buildings the error averaged 28 percentage points and one INCREASED its CO₂ by 103 %.',
        actionKey:
            'Commission an Energieberatung before quoting any CO₂ figure. The energy saving potential and the cost saving are unaffected and still apply.',
        params: { carrier: 'CARRIER', heatFactor: 'VALUE' },
    },
    // CO2-NA (wood → heat pump) — Calculator B70 with the live break-even
    // 1 − CO₂ Ist · 1000 / (demand estimate · f_elec).
    CO2_NOT_PREDICTED_BIOMASS_HEAT_PUMP: {
        titleKey: 'CO₂ not predicted: wood replaced by a heat pump',
        triggerKey:
            'The carrier {{carrier}} is replaced by a heat pump. On this building CO₂ only breaks even above {{breakEvenSavingPercent}} % energy saving.',
        // No NGF (nor BGF): the backend omits the break-even.
        triggerWithoutParam: {
            param: 'breakEvenSavingPercent',
            triggerKey: 'The carrier {{carrier}} is replaced by a heat pump.',
        },
        meaningKey:
            'This is NOT a thin-data problem: the answer genuinely straddles zero. The carrier {{carrier}} emits {{heatFactor}} kg CO₂e per kWh and grid electricity {{electricityFactor}}, so the building only comes out ahead if the renovation saves enough energy to more than cover that. Across the 65–92 % the heat-pump rule can produce, the CO₂ result for wood swings from about +180 % (much worse) to about −36 % (better). Which side you land on depends on exactly the number the model is least sure of, so no figure is shown.',
        actionKey: CO2_ONLY_ACTION_KEY,
        params: {
            carrier: 'CARRIER',
            heatFactor: 'VALUE',
            electricityFactor: 'VALUE',
            breakEvenSavingPercent: 'FIXED_1',
        },
    },
    // NO-PRECEDENT — the cost band's live price jump is Calculator B71's (p_heat
    // → p_elec and their ratio as params); "H1 and H2" → the energy saving (H2
    // trimmed).
    NO_REFERENCE_FOR_SWITCH: {
        titleKey: 'No reference building made this switch',
        triggerKey:
            'The default for {{carrier}} is to keep the generator, but the heat pump has been selected as the new heat generator.',
        meaningKey:
            'All 9 wood-fired and all 15 district-heated reference buildings kept their existing generator, so nothing in the data has done what is being asked here. The ENERGY SAVING is still on reasonable ground: it switches to the heat-pump rule, fitted on 36 buildings that replaced a gas or oil boiler, and a wood or oil boiler converts fuel to heat at roughly the same efficiency as a gas one. THE COST SAVING IS NOT: its offset and band were fitted where the price went from about 0.14 to 0.41 €/kWh, a factor of 3.0. Here it goes from {{heatPrice}} to {{electricityPrice}} €/kWh, a factor of {{priceRatio}}.',
        actionKey:
            "Treat the energy saving potential normally. Read the cost saving as a direction rather than a number, and enter the municipality's real heat and electricity prices before quoting it at all. CO₂ is suppressed for these carriers anyway, and for good reason — see the CO₂ notice.",
        params: { carrier: 'CARRIER', heatPrice: 'FIXED_3', electricityPrice: 'FIXED_3', priceRatio: 'FIXED_1' },
    },
    // USE-LOW
    BUILDING_BARELY_USED: {
        titleKey: 'Building barely used',
        triggerKey:
            'The use ratio — metered total divided by the demand estimated from size and condition — is {{useRatio}}, below {{min}}.',
        meaningKey:
            'A typical municipal building reads about 0.55, because an Energieberatungsbericht models standardised full occupancy and real buildings are used less. Below {{min}} is outside anything measured: either the meter reading is wrong (part year, wrong meter) or the building is hardly heated at all.',
        actionKey:
            'Check the meter first. If it is right, the building is barely in use — it will save far less in practice than the prediction implies, and is usually a poor candidate for an audit however good the numbers look.',
        params: { useRatio: 'FIXED_2', min: 'DECIMAL' },
    },
    // USE-HIGH
    CONSUMPTION_ABOVE_FABRIC: {
        titleKey: 'Consuming more than the fabric implies',
        triggerKey: 'The use ratio is {{useRatio}}, above {{max}}.',
        meaningKey:
            'The building burns more than its size and condition can account for. Either the meter covers more than this building (one reference meter served a whole site), or something operational is wrong — heating schedule, setpoints, controls, a leak.',
        actionKey:
            'Worth investigating before quoting an investment: operational faults are usually an order of magnitude cheaper to fix than fabric, and fixing them changes what the renovation is worth.',
        params: { useRatio: 'FIXED_2', max: 'DECIMAL' },
    },
    // SIZE? — trimmed: the investment estimate and cost-per-m² sentences (H2).
    BUILDING_SIZE_OUT_OF_RANGE: {
        titleKey: 'Building size outside the reference set',
        triggerKey:
            'NGF is {{netFloorArea}} m² — below {{min}} m² or above {{max}} m², outside every reference building.',
        meaningKey:
            'The 62 reference buildings run from {{min}} to {{max}} m² (half of them between 383 and 4,815). Outside that range the demand estimate that the cost and CO₂ figures (and, for district heating, the energy saving) stand on is extrapolating — the model has never seen a building this size.',
        actionKey: 'Treat the numbers as an order of magnitude only.',
        params: { netFloorArea: 'ROUNDED', min: 'ROUNDED', max: 'ROUNDED' },
    },
    // NO-METER — a kWh ≤ 0 entry counts as missing (the facts mapper drops it);
    // "The four predictions" → "The predictions" (H2 trimmed).
    NO_METER_READING: {
        titleKey: 'No meter reading entered',
        triggerKey: 'No reading entered for: {{missing}}.',
        meaningKey:
            'Not an error. The predictions are built from NGF, envelope condition, carrier and tariffs — none of them reads the meter — so they are unaffected.',
        actionKey:
            'What you lose is the sanity checking: without a meter you cannot tell whether the building is actually in use, whether the meter covers this building only, or whether the numbers hang together. Get a reading before quoting anything to a municipality.',
        derivedParams: (t, raw) => {
            const missing = Array.isArray(raw['missing']) ? (raw['missing'] as unknown[]) : [raw['missing']];
            return { missing: missing.map((meter) => enumParamLabel(t, MISSING_METER_LABEL_KEYS, meter)).join(', ') };
        },
    },
    // HEAT? — the action is Calculator B74's, widened to the cost figure (sheet
    // erratum: the Warnings tab claims every prediction depends on the meter,
    // none does — only a wrong NGF, which this check also exposes, reaches the
    // demand estimate behind CO₂ and the energy costs).
    HEAT_METER_IMPLAUSIBLE: {
        titleKey: 'Heat meter implausible',
        triggerKey:
            'Metered heat is {{meteredSpecificHeat}} kWh/(m²a) — below {{min}} or above {{max}} kWh per m² per year.',
        meaningKey:
            'Below {{min}} is almost always a part-year reading or the wrong meter. Above {{max}} means either the NGF or the meter is wrong. Three buildings in the reference data failed this test.',
        actionKey: 'Fix this before reading any CO₂ or cost figure.',
        params: { meteredSpecificHeat: 'FIXED_1', min: 'DECIMAL', max: 'DECIMAL' },
    },
    // ELEC-METER? — "the cost saving is distorted" adapted to what the meter
    // does reach: the implied-price check (the energy costs themselves are
    // estimate-based); the "CO2 Ist is overstated" clause is a sheet erratum
    // (CO₂ Ist is estimate-based too).
    ELECTRICITY_METER_SCOPE: {
        titleKey: 'Electricity meter may cover more than this building',
        triggerKey: 'Metered electricity is {{electricityToHeatRatio}} × the metered heat — more than {{max}} ×.',
        meaningKey:
            'One reference building read 38 times the modelled building electricity because its meter served a whole site rather than the one building.',
        actionKey:
            'Confirm the meter serves only this building. If it serves more, the implied-price check on the cost saving is distorted.',
        params: { electricityToHeatRatio: 'FIXED_2', max: 'DECIMAL' },
    },
    // admi-only: a missing carrier or a legacy OTHER stamp — no sheet carrier,
    // so no EVEBI heat price either (the cost saving needs an entered one).
    CARRIER_NOT_MODELLED: {
        titleKey: 'Energy carrier not in the prediction model',
        triggerKey: 'Energy carrier: {{carrier}} — not one of the eight carriers of the reference buildings.',
        meaningKey:
            'Without a switch to a heat pump there is no energy saving prediction; with the heat pump selected the heat-pump rule applies. CO₂ is never predicted, because the emission factor of the current carrier is unknown, and the cost saving needs its heat price.',
        actionKey:
            'Pick one of the six energy carriers, or enter the heat price for the cost saving; otherwise enter both bounds with a reason.',
        derivedParams: (t, raw) => ({
            carrier: raw['carrier'] == null ? t(UNKNOWN_CARRIER_KEY) : carrierLabel(t, String(raw['carrier'])),
        }),
    },
    // admi-only: a heating renewal with a non-heat-pump system — no switch, so
    // the energy and the cost saving follow the carrier's own rule.
    HEATING_SYSTEM_NOT_MODELLED: {
        titleKey: 'Heating system not in the prediction model',
        triggerKey:
            '{{heatingSystem}} is selected for the heating renewal — the model only knows the heat pump as a new heat generator.',
        meaningKey:
            'The energy saving potential and the cost saving are calculated as if the current heat generator were kept; CO₂ is not predicted because the new generator is missing from the model.',
        actionKey:
            'Read the energy saving potential and the cost saving as indicative only. For a CO₂ figure, enter both bounds with a reason.',
        params: { heatingSystem: 'HEATING_SYSTEM' },
    },
};

/** The four display texts of one prognosis warning; null parts for an unknown code. */
export interface PrognosisWarningTexts {
    title: string;
    trigger: string | null;
    meaning: string | null;
    action: string | null;
}

/**
 * The display texts of one `/calculate` prognosis warning (GEN-788): the
 * i18next messages of its stable code with the raw params formatted per the
 * active UI language — the trigger falls back to [WarningMessageSpec.triggerWithoutParam]
 * when the backend omitted that optional param. An unknown code (newer backend)
 * renders its code + raw params as the title and no further text — legible,
 * never a blank alert.
 */
export function warningTexts(
    t: TFunction,
    language: string | undefined,
    warning: PrognosisWarningDto,
): PrognosisWarningTexts {
    const spec = WARNING_MESSAGES[warning.code];
    if (spec === undefined) return { title: unknownCodeFallback(warning), trigger: null, meaning: null, action: null };
    const rawParams = warning.params ?? {};
    const formatted = formatSpecParams(t, language, spec, rawParams);
    const withoutParam = spec.triggerWithoutParam;
    const triggerKey =
        withoutParam !== undefined && rawParams[withoutParam.param] == null ? withoutParam.triggerKey : spec.triggerKey;
    return {
        title: t(spec.titleKey, formatted),
        trigger: t(triggerKey, formatted),
        meaning: t(spec.meaningKey, formatted),
        action: t(spec.actionKey, formatted),
    };
}

/** Count + worst severity of a run's warnings — the KPI header chip (GEN-788). */
export function prognosisWarningSummary(warnings: PrognosisWarningDto[] | null | undefined): {
    count: number;
    hasWarning: boolean;
} {
    const list = warnings ?? [];
    return { count: list.length, hasWarning: list.some((warning) => warning.severity === 'WARNING') };
}

/** True when a SUPPRESSED warning removes `metric` — the panel then reads "not predicted", not "inputs missing". */
export function isMetricSuppressed(
    warnings: PrognosisWarningDto[] | null | undefined,
    metric: PrognosisMetricKey,
): boolean {
    return (warnings ?? []).some(
        (warning) => warning.severity === 'SUPPRESSED' && (warning.affectedMetrics ?? []).includes(metric),
    );
}

/** The translated labels of the metrics a warning questions or suppresses (unknown names verbatim). */
export function warningMetricLabels(t: TFunction, warning: PrognosisWarningDto): string[] {
    return (warning.affectedMetrics ?? []).map((name) => {
        const metric = PROGNOSIS_METRICS.find((m) => m.metric === name);
        return metric === undefined ? name : t(metric.labelKey);
    });
}
